import { useEffect } from 'react';

import { Stack, Tooltip } from '@mui/material';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import Button from 'domains/core/components/Button';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';

type Props = {
    activeStep: CustomCampaignSteps;
    continueTooltip?: string;
    isContinueEnabled: boolean;
    isPublishEnabled?: boolean;
    publishDisabledTooltip?: string;
    onContinueClick: () => void;
    onPublishClick: () => void;
    onPublishAndResumeClick: () => void;
    onSaveDraftClick: () => void;
    setContinueTooltip: (tooltip: string) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
};

// Reusable Save as Draft button component
const SaveAsDraftButton = ({ onClick }: { onClick: () => void }) => (
    <Button onClick={onClick} variant="outlined" sx={{ width: '146px' }} data-testid="save-as-draft-button">
        Save as Draft
    </Button>
);

/**
 *
 * @param onClick - The function to call when the button is clicked.
 * @returns A button that when clicked, will call the onClick function which will be resume functionality.
 */
const ResumeButton = ({
    disabled,
    disabledTooltip,
    onClick,
}: {
    disabled?: boolean;
    disabledTooltip?: string;
    onClick: () => void;
}) => (
    // disableInteractive prevents the tooltip popper from getting `pointer-events: auto`,
    // which otherwise overlaps and blocks clicks on the adjacent Save button when Resume
    // is hovered/focused. Reproed locally: Playwright's pre-click scroll-into-view focuses
    // Resume, opens the tooltip, then the next click on Save is intercepted by the tooltip
    // popper subtree. The qa-outreach "Click X Button by role" step works around this with
    // a DOM .click() fallback, but the symptom is real for human users too: clicking Save
    // right after grazing Resume sometimes does nothing.
    <Tooltip title={disabled ? disabledTooltip : 'Publish changes and resume curation'} arrow disableInteractive>
        <span>
            <Button
                onClick={onClick}
                disabled={disabled}
                variant="contained"
                data-testid="resume-button"
                aria-label={disabled ? `Resume, disabled: ${disabledTooltip}` : undefined}
            >
                Resume
            </Button>
        </span>
    </Tooltip>
);

const CustomCampaignNavigationButtons = ({
    activeStep,
    continueTooltip = '',
    isContinueEnabled,
    isPublishEnabled = true,
    publishDisabledTooltip = '',
    onContinueClick,
    onPublishClick,
    onPublishAndResumeClick,
    onSaveDraftClick,
    setContinueTooltip,
    setIsContinueEnabled,
}: Props) => {
    const { canPublishCurations, canManageCurationsDrafts } = useAccessCentrePermissions();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    useEffect(() => {
        // Disabling the continue button with every step change
        // as every step has its own unique validation requirements
        setIsContinueEnabled(false);
        // Reset tooltip so disabled state on other steps shows no tooltip
        setContinueTooltip('');
    }, [activeStep, setContinueTooltip, setIsContinueEnabled]);

    const hideNavigationButtons =
        activeStep === CustomCampaignSteps.CHOOSE_NEXT_STEP ||
        activeStep === CustomCampaignSteps.CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT ||
        activeStep === CustomCampaignSteps.SELECT_PATH_ACTION;
    if (hideNavigationButtons) return;

    if (activeStep === CustomCampaignSteps.PUBLISH_CAMPAIGN) {
        // For paused curations: Save on left, Resume on right
        if (status === CampaignStatus.PAUSED) {
            return (
                <Stack direction="row" spacing={2}>
                    {canPublishCurations && (
                        <Tooltip title={!isPublishEnabled ? publishDisabledTooltip : ''} arrow>
                            <span>
                                <Button
                                    onClick={onPublishClick}
                                    disabled={!isPublishEnabled}
                                    variant="outlined"
                                    aria-label={
                                        !isPublishEnabled ? `Save, disabled: ${publishDisabledTooltip}` : undefined
                                    }
                                >
                                    Save
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {canPublishCurations && (
                        <ResumeButton
                            disabled={!isPublishEnabled}
                            disabledTooltip={publishDisabledTooltip}
                            onClick={onPublishAndResumeClick}
                        />
                    )}
                </Stack>
            );
        }

        // For other curations: Save as Draft (optional), Publish
        return (
            <Stack direction="row" spacing={2}>
                {canManageCurationsDrafts && <SaveAsDraftButton onClick={onSaveDraftClick} />}
                {canPublishCurations && (
                    <Tooltip title={!isPublishEnabled ? publishDisabledTooltip : ''} arrow>
                        <span>
                            <Button
                                onClick={onPublishClick}
                                disabled={!isPublishEnabled}
                                variant="outlined"
                                data-testid="publish-button"
                                aria-label={
                                    !isPublishEnabled ? `Publish, disabled: ${publishDisabledTooltip}` : undefined
                                }
                            >
                                Publish
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Stack>
        );
    }

    return (
        <Stack direction="row" spacing={2}>
            {canManageCurationsDrafts &&
                status !== CampaignStatus.PAUSED &&
                activeStep !== CustomCampaignSteps.NAME_CAMPAIGN && <SaveAsDraftButton onClick={onSaveDraftClick} />}
            <Tooltip title={!isContinueEnabled && continueTooltip ? continueTooltip : ''} arrow placement="bottom">
                <span>
                    <Button
                        data-testid="continue-button"
                        disabled={!isContinueEnabled}
                        onClick={onContinueClick}
                        variant="outlined"
                    >
                        Continue
                    </Button>
                </span>
            </Tooltip>
        </Stack>
    );
};

export default CustomCampaignNavigationButtons;
